import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { formatDocument, isValidCpfCnpj, normalizeDocument } from '../../domain/document'
import { errorMessage } from '../../lib/errors'
import type { CatalogKind, Client } from '../../types/domain'
import {
  createCatalogRecord,
  listCatalog,
  setCatalogActive,
  updateCatalogRecord,
  type CatalogInput,
} from './catalog.repository'

const labels: Record<CatalogKind, { singular: string; plural: string }> = {
  clients: { singular: 'cliente', plural: 'Clientes' },
  assignees: { singular: 'responsável', plural: 'Responsáveis' },
  departments: { singular: 'departamento', plural: 'Departamentos' },
}

function CatalogSection({ kind }: { kind: CatalogKind }) {
  const queryClient = useQueryClient()
  const [name, setName] = useState('')
  const [document, setDocument] = useState('')
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editName, setEditName] = useState('')
  const [editDocument, setEditDocument] = useState('')
  const [feedback, setFeedback] = useState('')
  const [validationError, setValidationError] = useState('')
  const catalog = useQuery({
    queryKey: ['catalog', kind],
    queryFn: () => listCatalog(kind),
  })
  const createMutation = useMutation({
    mutationFn: (input: CatalogInput) => createCatalogRecord(kind, input),
    onSuccess: async () => {
      setName('')
      setDocument('')
      setFeedback(`${labels[kind].singular} salvo com sucesso.`)
      await queryClient.invalidateQueries({ queryKey: ['catalog', kind] })
    },
  })
  const editMutation = useMutation({
    mutationFn: ({ id, input }: { id: string; input: CatalogInput }) =>
      updateCatalogRecord(kind, id, input),
    onSuccess: async () => {
      setEditingId(null)
      setFeedback(`${labels[kind].singular} atualizado com sucesso.`)
      await queryClient.invalidateQueries({ queryKey: ['catalog', kind] })
    },
  })
  const toggleMutation = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      setCatalogActive(kind, id, active),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['catalog', kind] }),
  })

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setFeedback('')
    setValidationError('')
    const digits = normalizeDocument(document)
    if (kind === 'clients' && digits && !isValidCpfCnpj(digits)) {
      setValidationError('Informe um CPF ou CNPJ válido.')
      return
    }
    if (name.trim()) createMutation.mutate({ name, document })
  }

  function submitEdit(event: FormEvent<HTMLFormElement>, id: string) {
    event.preventDefault()
    setFeedback('')
    setValidationError('')
    const digits = normalizeDocument(editDocument)
    if (kind === 'clients' && digits && !isValidCpfCnpj(digits)) {
      setValidationError('Informe um CPF ou CNPJ válido.')
      return
    }
    if (editName.trim()) editMutation.mutate({ id, input: { name: editName, document: editDocument } })
  }

  const mutationError = createMutation.error ?? editMutation.error ?? toggleMutation.error

  return (
    <section className="card p-5 sm:p-6">
      <h2 className="text-xl font-extrabold text-slate-900">{labels[kind].plural}</h2>
      <form className="mt-4 grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto]" onSubmit={submit}>
        <label className="field flex-1">
          <span>Novo {labels[kind].singular}</span>
          <input className="control" value={name} onChange={(event) => setName(event.target.value)} maxLength={120} required />
        </label>
        <button className="btn-primary self-end" disabled={createMutation.isPending} type="submit">Adicionar</button>
        {kind === 'clients' ? (
          <label className="field sm:col-span-2">
            <span>CPF ou CNPJ (opcional)</span>
            <input
              className="control"
              inputMode="numeric"
              value={formatDocument(document)}
              onChange={(event) => setDocument(normalizeDocument(event.target.value))}
              placeholder="000.000.000-00"
            />
          </label>
        ) : null}
      </form>
      {feedback ? <p className="notice-success mt-3" role="status">{feedback}</p> : null}
      {validationError ? <p className="notice-error mt-3" role="alert">{validationError}</p> : null}
      {mutationError ? <p className="notice-error mt-3" role="alert">{errorMessage(mutationError)}</p> : null}
      {catalog.isError ? <p className="notice-error mt-3" role="alert">{errorMessage(catalog.error)}</p> : null}
      <ul className="mt-5 divide-y divide-slate-200">
        {catalog.data?.map((record) => {
          const isProtected = (kind === 'clients' && record.name === 'Interno — Escritório')
            || (kind === 'departments' && record.name === 'Geral')
          const clientDocument = kind === 'clients' ? (record as Client).document : null
          return (
            <li className="py-3" key={record.id}>
              {editingId === record.id ? (
                <form className="grid gap-3" onSubmit={(event) => submitEdit(event, record.id)}>
                  <label className="field"><span>Nome</span><input className="control" maxLength={120} required value={editName} onChange={(event) => setEditName(event.target.value)} /></label>
                  {kind === 'clients' ? <label className="field"><span>CPF ou CNPJ (opcional)</span><input className="control" inputMode="numeric" value={formatDocument(editDocument)} onChange={(event) => setEditDocument(normalizeDocument(event.target.value))} /></label> : null}
                  <div className="flex flex-wrap gap-2">
                    <button className="btn-primary text-sm" disabled={editMutation.isPending} type="submit">Salvar alteração</button>
                    <button className="btn-secondary text-sm" onClick={() => setEditingId(null)} type="button">Cancelar</button>
                  </div>
                </form>
              ) : (
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <span className="font-semibold text-slate-800">{record.name}</span>
                    <span className={`ml-2 text-xs font-bold ${record.is_active ? 'text-emerald-700' : 'text-slate-500'}`}>
                      {record.is_active ? 'Ativo' : 'Inativo'}
                    </span>
                    {clientDocument ? <small className="mt-1 block text-slate-500">{formatDocument(clientDocument)}</small> : null}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      className="btn-secondary shrink-0 text-sm"
                      disabled={isProtected}
                      onClick={() => {
                        setEditingId(record.id)
                        setEditName(record.name)
                        setEditDocument(clientDocument ?? '')
                      }}
                      type="button"
                    >Editar</button>
                    <button
                      className="btn-secondary shrink-0 text-sm"
                      disabled={toggleMutation.isPending || isProtected}
                      onClick={() => toggleMutation.mutate({ id: record.id, active: !record.is_active })}
                      type="button"
                    >
                      {record.is_active ? 'Desativar' : 'Reativar'}
                    </button>
                  </div>
                </div>
              )}
            </li>
          )
        })}
      </ul>
      {!catalog.isLoading && catalog.data?.length === 0 ? <p className="mt-4 text-slate-500">Nenhum registro.</p> : null}
    </section>
  )
}

export function CatalogPage() {
  return (
    <div>
      <div className="mb-6">
        <p className="text-sm font-bold uppercase tracking-wider text-blue-700">Configuração</p>
        <h1 className="text-3xl font-extrabold text-slate-950">Cadastros</h1>
        <p className="mt-1 text-slate-600">Mantenha as listas usadas no registro de tarefas.</p>
      </div>
      <div className="grid gap-5 xl:grid-cols-3">
        <CatalogSection kind="clients" />
        <CatalogSection kind="assignees" />
        <CatalogSection kind="departments" />
      </div>
    </div>
  )
}
